import Image from '@/components/common/SpiritoryImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faRightToBracket, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import styles from './buyerGuide.module.scss';
import Link from 'next/link';
import translationFallback from '@/utilites/translations/translationFallback';
import { useTranslation } from 'next-i18next';
import SearchInGuides from '@/components/common/searchInGuides';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Accordion from 'react-bootstrap/Accordion';
import useBackendUser from '@/hooks/useBackendUser';
import getBasicStaticSideProps from '@/lib/staticSideProps/getBasicStaticSideProps';
import BuyersGuideStructuredData from '../components/structured_data/buyer_guide_structured_data';
import getCanonicalUrl from '@/utilites/seo/get-canonical-url';
import LanguageAlternates from '@/components/seo/LanguageAlternates';

function BuyerGuide() {
  const { data: user } = useBackendUser();
  const { t } = useTranslation('buyer_guide');

  const router = useRouter();
  const canonicalUrl = getCanonicalUrl(router);

  return (
    <>
      <Head>
        <title>
          {t('buyer_guide_title')}
        </title>
        <meta
          name="description"
          content={t('buyer_guide_description')}
        />
        <meta
          name="keywords"
          content={t('buyer_guide_keywords')}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t('buyer_guide_title')} />
        <meta property="og:description" content={t('buyer_guide_description')} />
        <meta property="og:image" content="https://spiritory.com/logo.png" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Spiritory" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('buyer_guide_title')} />
        <meta name="twitter:description" content={t('buyer_guide_description')} />
        <meta name="twitter:image" content="https://spiritory.com/logo.png" />
        <meta name="twitter:site" content="@spiritoryGlobal" />
        <link rel="canonical" href={canonicalUrl} />
        <LanguageAlternates />
        <BuyersGuideStructuredData />
      </Head>
      <div className={`lh-25 ${styles.mainContainer}`}>
        <div
          id="buyer-guide-section1"
          className={`bg-blue-gradient d-flex align-items-center mw-auto overflow-hidden max-nine:tw-gap-4 ${styles.mainSectionMobile}`}
          style={{ height: '100vh', width: '100%' }}
        >
          <div className={`${styles.mainText}`}>
            <div className={`tw-flex tw-align-items-start align-items-center max-nine:tw-justify-center`}>
              <h1 className="h2 tw-pr-3 !tw-mb-0 max-nine:tw-text-center">
                {t('buyer_guide_section.buyer_guide_title')}
              </h1>
            </div>

            <div className={`tw-m-auto tw-flex tw-flex-col max-nine:tw-justify-center max-nine:tw-items-center tw-gap-4`}>
              <div className="tw-flex max-nine:tw-justify-center tw-items-center">
                <FontAwesomeIcon icon={faCheck} className="me-2 text-primary" />
                <p className="font-size-20 lh-25 tw-mb-0">
                  {t('buyer_guide_section.simple_secure_user_friendly')}
                </p>
              </div>
              <div className={`tw-flex max-nine:tw-justify-center ${styles.btnContainer}`}>
                <div
                  onClick={() => {
                    if (user) {
                      router.push('/dashboard');
                    } else {
                      router.push('/auth/sign-in');
                    }
                  }}
                >
                  <button className={`btn btn-s-outline-primary`} type="button">
                    <span>
                      <FontAwesomeIcon icon={faRightToBracket} />
                    </span>
                    {translationFallback('login', t('login'), 'Log In')}
                  </button>
                </div>
                <div
                  onClick={() => {
                    if (user) {
                      router.push('/dashboard');
                    } else {
                      router.push('/auth/sign-up');
                    }
                  }}
                >
                  <button
                    className={`btn btn-s-primary`}
                    type="button"
                    style={{ marginLeft: '12px' }}
                  >
                    <span>
                      <FontAwesomeIcon icon={faUserPlus} />
                    </span>
                    {translationFallback('signup', t('signup'), 'Sign Up')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className={`${styles.mainImageContainer} ${styles.hideDesktop}`}>
            <Image
              src="/images/buy-guide/mainImageMobile.webp"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
              className={styles.specialImageMain}
            />
            <Image
              src="/images/buy-guide/buyer-guide2.svg"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
            />
          </div>
          <div className={` ${styles.mainImageContainer} ${styles.hideMobile}`}>
            <Image
              src="/images/buy-guide/buyer-guide.svg"
              alt="example"
              width={590}
              height={396}
              sizes="100vw"
            />
            <Image
              src="/images/buy-guide/buyer-guide2.svg"
              alt="example"
              width={538}
              height={397.37}
              sizes="100vw"
            />
          </div>
        </div>

        <div
          className={`py-9 d-flex align-items-center justify-content-between gap-6 ${styles.firstSectionImage}`}
        >
          <div>
            <Image
              src="/images/sell-guide/section1.svg"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
            />
          </div>
          <div className="position-relative">
            <span className={styles.twoNumber}>1</span>
            <div style={{ width: 'fit-content' }} className="d-flex flex-column align-items-start">
              <h2
                className="h3 mt-3"
                style={{
                  fontWeight: '700',
                  fontSize: '32px',
                }}
              >
                1. {t('buyer_guide_section.finding_your_bottle_title')}
              </h2>
              <div
                className={`mt-2 ${styles.borderDown}`}
                style={{ width: '70%', height: 4, backgroundColor: '#C2AD7D' }}
              ></div>
            </div>
            <div className={`mt-3 ${styles.secondSectionText}`}>
              <div className="d-flex mb-2">
                {translationFallback(
                  'buyer_guide_section.finding_your_bottle_description',
                  t('buyer_guide_section.finding_your_bottle_description'),
                  'Simply browse our growing marketplace and select the bottle you`re looking to sell.'
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className={`pb-9 d-flex align-items-center justify-content-between gap-8 ${styles.secondSectionImage}`}
        >
          <div className="position-relative">
            <span className={styles.oneNumber}>2</span>
            <div style={{ width: 'fit-content' }} className="d-flex flex-column align-items-start">
              <h2
                className="h3 mt-3"
                style={{
                  fontWeight: '700',
                  fontSize: '32px',
                  lineHeight: '40px',
                }}
              >
                {translationFallback(
                  'buyer_guide_section.the_product_page_title',
                  t('buyer_guide_section.the_product_page_title'),
                  '2.The product page'
                )}
              </h2>
              <div
                className={`mt-2 ${styles.borderDown}`}
                style={{ width: '70%', height: 4, backgroundColor: '#C2AD7D' }}
              ></div>
            </div>
            <div className={`mt-3 ${styles.secondSectionText}`}>
              <div className="d-flex mb-2">
                <p className="font-size-20 lh-23">
                  {translationFallback(
                    'buyer_guide_section.decide_how_to_sell',
                    t('buyer_guide_section.decide_how_to_sell'),
                    `Once you've found your desired bottle, you can decide on how you want to sell it. Your two options are to either "sell now" or "ask" . We will explain the two options now:`
                  )}
                </p>
              </div>
            </div>
          </div>
          <div>
            <Image
              src="/images/sell-guide/section2.svg"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
            />
          </div>
        </div>

        <div
          style={{
            background: '#1C5CEB',
            padding: '6rem 1rem',
          }}
          className={styles.blueSection}
        >
          <div className="h2 py-5">
            <h2>
              {translationFallback(
                'buyer_guide_section.option_1_title',
                t('buyer_guide_section.option_1_title'),
                'Option 1'
              )}
            </h2>
          </div>
          <div
            className={`pt-9 d-flex align-items-center justify-content-between gap-8 ${styles.firstSectionImage}`}
            style={{ paddingTop: 0 }}
          >
            <div className={styles.specialImage}>
              <Image
                src="/images/buy-guide/option2bot.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
              />
              <Image
                src="/images/buy-guide/option1Tab.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
              />
            </div>
            <div className="position-relative d-flex flex-column">
              <span className={styles.twoNumber} style={{ color: '#FFFFFF33' }}>
                3
              </span>
              <div
                style={{ width: 'fit-content' }}
                className="d-flex flex-column align-items-start"
              >
                <h2
                  className="h3 mt-3 font-jakarta"
                  style={{
                    fontWeight: '700',
                    fontSize: '32px',
                    lineHeight: '40px',
                  }}
                >
                  {translationFallback(
                    'buyer_guide_section.buy_now_title',
                    t('buyer_guide_section.buy_now_title'),
                    '3. Buy now'
                  )}
                </h2>
                <div
                  className="mt-2"
                  style={{
                    width: '70%',
                    height: 4,
                    backgroundColor: '#C2AD7D',
                  }}
                ></div>
              </div>
              <div className="mt-3">
                <div className="d-flex mb-2">
                  <p className="font-size-20 lh-23">
                    {translationFallback(
                      'buyer_guide_section.buy_instantly_lowest_ask',
                      t('buyer_guide_section.buy_instantly_lowest_ask'),
                      'Buy instantly at the lowest "Ask" of a seller'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="h2 py-5" style={{ marginTop: '3rem' }}>
            <h2>
              {translationFallback(
                'buyer_guide_section.option_2_title',
                t('buyer_guide_section.option_2_title'),
                'Option 2'
              )}
            </h2>
          </div>
          <div
            className={`d-flex align-items-center justify-content-between gap-8 ${styles.secondSectionImage}`}
          >
            <div className="position-relative">
              <span className={styles.oneNumber} style={{ color: '#FFFFFF33', zIndex: 1 }}>
                4
              </span>
              <div
                style={{ width: 'fit-content' }}
                className="d-flex flex-column align-items-start"
              >
                <h2
                  className="h3 mt-3"
                  style={{
                    fontWeight: '700',
                    fontSize: '32px',
                    lineHeight: '40px',
                  }}
                >
                  {translationFallback(
                    'buyer_guide_section.place_bid_title',
                    t('buyer_guide_section.place_bid_title'),
                    '4. Place Bid'
                  )}
                </h2>
                <div
                  className={`mt-2 ${styles.borderDown}`}
                  style={{
                    width: '70%',
                    height: 4,
                    backgroundColor: '#C2AD7D',
                  }}
                ></div>
              </div>
              <div className={`mt-3 ${styles.secondSectionText}`}>
                <div className="d-flex mb-2">
                  <p className="font-size-20 lh-23">
                    {translationFallback(
                      'buyer_guide_section.place_bid_description',
                      t('buyer_guide_section.place_bid_description'),
                      'Start your first trades with these easy steps'
                    )}
                  </p>
                </div>
                <ul className="font-size-20 lh-25">
                  <li className="py-1">
                    {translationFallback(
                      'buyer_guide_section.bid_step_1',
                      t('buyer_guide_section.bid_step_1'),
                      `When placing an "bid" , you decide the price you're buying at.`
                    )}
                  </li>
                  <li className="py-1">
                    {translationFallback(
                      'buyer_guide_section.bid_step_2',
                      t('buyer_guide_section.bid_step_2'),
                      'If you have the highest bid, we will match you with a seller, willing to sell at that price.'
                    )}
                  </li>
                  <li className="py-1">
                    {translationFallback(
                      'buyer_guide_section.bid_step_3',
                      t('buyer_guide_section.bid_step_3'),
                      'You can always adjust your bid, in case no buyer is found at that price.'
                    )}
                  </li>
                </ul>
              </div>
            </div>
            <div className={`${styles.specialImageTwo}`}>
              <Image
                src="/images/buy-guide/option2bot.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
              />
              <Image
                src="/images/buy-guide/option2Tab.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
              />
            </div>
          </div>
        </div>

        <div
          className={`py-9 d-flex align-items-center justify-content-between gap-6 ${styles.firstSectionImage}`}
        >
          <div>
            <Image
              src="/images/buy-guide/section5.svg"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
            />
          </div>
          <div className="position-relative">
            <span className={styles.twoNumber}>5</span>
            <div style={{ width: 'fit-content' }} className="d-flex flex-column align-items-start">
              <h2
                className="h3 mt-3"
                style={{
                  fontWeight: '700',
                  fontSize: '32px',
                  lineHeight: '40px',
                }}
              >
                {translationFallback(
                  'buyer_guide_section.deal_made_title',
                  t('buyer_guide_section.deal_made_title'),
                  '5. Deal made?'
                )}
              </h2>
              <div
                className={`mt-2 ${styles.borderDown}`}
                style={{ width: '70%', height: 4, backgroundColor: '#C2AD7D' }}
              ></div>
            </div>
            <div className={`mt-3 ${styles.secondSectionText}`}>
              <div className="d-flex mb-2">
                {translationFallback(
                  'buyer_guide_section.deal_made_description',
                  t('buyer_guide_section.deal_made_description'),
                  "Great, now it's our turn! We make sure everything is safe and secure for our buyers. Find out about how we do that in our next step."
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className={`pb-9 d-flex align-items-center justify-content-between gap-8 ${styles.secondSectionImage}`}
        >
          <div className="position-relative">
            <span className={styles.oneNumber}>6</span>
            <div style={{ width: 'fit-content' }} className="d-flex flex-column align-items-start">
              <h2
                className="h3 mt-3"
                style={{
                  fontWeight: '700',
                  fontSize: '32px',
                  lineHeight: '40px',
                }}
              >
                {translationFallback(
                  'buyer_guide_section.pay_safe_title',
                  t('buyer_guide_section.pay_safe_title'),
                  '6. Pay safe'
                )}
              </h2>
              <div
                className={`mt-2 ${styles.borderDown}`}
                style={{ width: '70%', height: 4, backgroundColor: '#C2AD7D' }}
              ></div>
            </div>
            <div className={`mt-3 ${styles.secondSectionText}`}>
              <div className="d-flex mb-2">
                <p className="font-size-20 lh-23">
                  {translationFallback(
                    'buyer_guide_section.pay_safe_description',
                    t('buyer_guide_section.pay_safe_description'),
                    "Only if the bottle successfully passes our authentication, you will be charged. That's how we ensure everything is okay with the bottle. You pay, the buyer ships and is paid when you've confirmed the safe arrival."
                  )}
                </p>
              </div>
            </div>
            <div
              id={styles.buttonContainerId}
              className={`d-flex ${styles.buttonContainer} align-items-baseline`}
            >
              <Link href="/shipping-guide">
                <button
                  className={`btn btn-s-outline-primary font-size-15 ${styles.btnSize} ${styles.whiteBtn} ${styles.whiteBtnB}`}
                  type="button"
                >
                  {translationFallback(
                    'buyer_guide_section.view_shipping_guide',
                    t('buyer_guide_section.view_shipping_guide'),
                    'View Shipping guide'
                  )}
                </button>
              </Link>
              <Link href="/shipping-guide">
                <button
                  className={`btn btn-s-primary font-size-15 ${styles.btnSizeB} ${styles.whiteBtn}`}
                  type="button"
                >
                  {translationFallback(
                    'buyer_guide_section.track_order_status',
                    t('buyer_guide_section.track_order_status'),
                    'Track status of your order'
                  )}
                </button>
              </Link>
            </div>
          </div>
          <div>
            <Image
              src="/images/buy-guide/section9.svg"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
            />
          </div>
        </div>

        <div
          id="seller-Section10"
          className={` ${styles.textwhite} ${styles.greenContainer} px-0 py-6`}
          style={{ height: 'auto', backgroundColor: '#497def' }}
        >
          <div
            style={{ width: 'fit-content', margin: '0 auto' }}
            className={`container d-flex flex-column justify-content-center align-items-center mb-6 mt-2`}
          >
            <h2 className={`h2 text-center`}>
              {translationFallback(
                'buyer_guide_section.what_makes_special',
                t('buyer_guide_section.what_makes_special'),
                'What makes buying with us special:'
              )}
            </h2>
            <div
              className="mt-2"
              style={{ width: '70%', height: 4, backgroundColor: '#C2AD7D' }}
            ></div>
          </div>
          <div
            className={`d-flex justify-content-center align-items-center ${styles.allCards}`}
            style={{ gap: '32px' }}
          >
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri1.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-2">
                {translationFallback(
                  'buyer_guide_section.transparency_on_fees',
                  t('buyer_guide_section.transparency_on_fees'),
                  '100% transparency on all fees'
                )}
              </p>
            </div>
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri2.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-2">
                {translationFallback(
                  'buyer_guide_section.simple_quick_authentication',
                  t('buyer_guide_section.simple_quick_authentication'),
                  'Simple and quick authentication'
                )}
              </p>
            </div>
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri3.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-2">
                {translationFallback(
                  'buyer_guide_section.safe_secure_payout_process',
                  t('buyer_guide_section.safe_secure_payout_process'),
                  'Safe and secure payout process'
                )}
              </p>
            </div>
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri4.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-4 text-center">
                {translationFallback(
                  'buyer_guide_section.in_depth_pricing_data',
                  t('buyer_guide_section.in_depth_pricing_data'),
                  'In depth pricing data and sales statistics'
                )}
              </p>
            </div>
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri1.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-4 text-center">
                {translationFallback(
                  'buyer_guide_section.sell_buy_quickly',
                  t('buyer_guide_section.sell_buy_quickly'),
                  'Sell and buy in just a few seconds, no hassle'
                )}
              </p>
            </div>
          </div>
        </div>
        <SearchInGuides
          becomeCallToAct={translationFallback(
            'buyer_guide_section.find_dream_bottle',
            t('buyer_guide_section.find_dream_bottle'),
            'Find your dream bottle today'
          )}
          becomeTitle={translationFallback(
            'buyer_guide_section.become_buyer_now',
            t('buyer_guide_section.become_buyer_now'),
            'Become a Buyer right now'
          )}
          userId={user?.id ?? null}
        />
        <div id="buyer-guide-section10" className="container px-sm-8 py-6 py-sm-7">
          <div className={`h3 ${styles.bold}`}>
            {translationFallback(
              'buyer_guide_section.frequently_asked_questions',
              t('buyer_guide_section.frequently_asked_questions'),
              'Frequently asked questions'
            )}
          </div>

          <Accordion defaultActiveKey="0" className={'my-4'}>
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq1_q',
                    t('faqs.faq1_q'),
                    'Do I need to create an account to make a purchase?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqs.faq1_a',
                  t('faqs.faq1_a'),
                  'Yes, in order to make a purchase or sell a bottle on Spiritory you have to register as a user. You can do so here, or at the top right of the page in “Sign-in”. '
                )}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq2_q',
                    t('faqs.faq2_q'),
                    'Can I use Spiritory from any country?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqs.faq2_a',
                  t('faqs.faq2_a'),
                  'Spiritory is currently available in European Union countries.'
                )}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="2">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq3_q',
                    t('faqs.faq3_q'),
                    'How "place a bid" works? '
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqs.faq3_a',
                  t('faqs.faq3_a'),
                  'When bidding on a product, the buyer can suggest a price for the bottle, this offer can last from 7 to 60 days. This offer is automatically sent to the seller. The seller can accept or refuse this price and the user will be notified with his decision. All in a transparent way for buyers and sellers. '
                )}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="3">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq4_q',
                    t('faqs.faq4_q'),
                    'I would like to buy this bottle. How do I proceed?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq4_a',
                      t('faqs.faq4_a'),
                      '"You have two options to purchase a bottle:<br>' +
                        '1. Buy Now: Click on the “Buy Now” button to accept the listed price. Once you confirm the purchase, the authentication process will begin, which typically takes between 1 to 48 hours. After the bottle is authenticated, you’ll receive a notification and can proceed with the payment.<br>' +
                        '2. Place a Bid: Click on “Place a Bid” to propose your own price for the bottle. The seller can either accept or reject your offer. If the seller accepts, the same process follows: authentication of the bottle and then payment.<br>' +
                        'Both methods ensure transparency for both buyers and sellers."'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="4">
              <Accordion.Header>
                <b>
                  {translationFallback('faqs.faq5_q', t('faqs.faq5_q'), 'When do I have to pay ?')}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq5_a',
                      t('faqs.faq5_a'),
                      'After placing an order, the seller must start the authentication process of the bottle, which can take from 1 to 48 hours, after passing the verification by our team, you will be informed that you can proceed with the payment. '
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="5">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq6_q',
                    t('faqs.faq6_q'),
                    'How can I get an invoice for my purchase?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq6_a',
                      t('faqs.faq6_a'),
                      'The Invoice can be found in the Offer Status in the Dashboard an can be downloaded after the payment was successful!'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="6">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq7_q',
                    t('faqs.faq7_q'),
                    'Does Spiritory charge additional taxes?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq7_a',
                      t('faqs.faq7_a'),
                      'No, Spiritory only includes the fees established for the seller and the buyer as a marcketplace. '
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="7">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq8_q',
                    t('faqs.faq8_q'),
                    'How can I apply a discount coupon?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq8_a',
                      t('faqs.faq8_a'),
                      'A discount coupon can be applied in the payment screen. There you have an extra field where you can input your coupon code'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="8">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq9_q',
                    t('faqs.faq9_q'),
                    'Can I get a refund if I no longer want the bottle?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq9_a',
                      t('faqs.faq9_a'),
                      'Refunds are available if the return request meets our policies for unopened bottles from commercial sellers. Refunds are not granted for private seller transactions or opened bottles'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="9">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq10_q',
                    t('faqs.faq10_q'),
                    'What is the Spiritory buyer protection? '
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq10_a',
                      t('faqs.faq10_a'),
                      'Payment on Spiritory is absolutely secure: we hold the purchase amount in our deposit account for up to 14 days. Your money is safe until you receive the bottles.'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <div
          id="buyer-guide-section11"
          className={`my-8 position-relative ${styles.lastSection}`}
          style={{
            backgroundColor: '#1C5CEB',
            color: '#FFFFFF',
            height: '300px',
            display: 'flex',
            overflow: 'hidden',
            gap: '1rem',
          }}
        >
          <div
            className={`d-flex justify-content-between align-items-center gap-3 position-relative ${styles.mainTextLastSection}`}
            style={{ width: '100%' }}
          >
            <div style={{ marginLeft: '62px', padding: '1rem 0', width: '50%' }}>
              <h2 className="h2 text-start">
                {translationFallback(
                  'signup_section.sign_up_today',
                  t('signup_section.sign_up_today'),
                  'Sign Up Today'
                )}
              </h2>
              <h3 className="text-start !tw-text-[1.25rem]" style={{ marginBottom: '2rem' }}>
                {translationFallback(
                  'signup_section.quality_assurance',
                  t('signup_section.quality_assurance'),
                  'With us you can be sure of the quality of the products you buy'
                )}
              </h3>
              <div className={`d-flex flex-row ${styles.buttonSection}`}>
                <button
                  onClick={() => {
                    if (user) {
                      router.push('/dashboard');
                    } else {
                      router.push('/auth/sign-up');
                    }
                  }}
                  className={`btn btn-light font-size-20 lh-25 ${styles.whiteBtnC} ${styles.customButton}`}
                  style={{ marginLeft: '0rem', background: 'transparent' }}
                >
                  <Link
                    href={'#'}
                    style={{
                      textDecoration: 'none',
                      color: '#FFFFFF',
                      fontWeight: '700',
                      marginLeft: '0rem',
                    }}
                  >
                    {translationFallback(
                      'signup_section.sign_up_now',
                      t('signup_section.sign_up_now'),
                      'Sign up Now'
                    )}
                  </Link>
                </button>
                <button
                  onClick={() => {
                    if (user) {
                      router.push('/dashboard');
                    } else {
                      router.push('/auth/sign-in');
                    }
                  }}
                  className={`btn btn-light font-size-20 lh-25 ${styles.whiteBtn} ${styles.customButton2}`}
                  style={{ border: 'none' }}
                >
                  <Link
                    href={'#'}
                    style={{
                      textDecoration: 'none',
                      color: '#1C5CEB',
                      fontWeight: '700',
                    }}
                  >
                    {translationFallback(
                      'signup_section.log_in_now',
                      t('signup_section.log_in_now'),
                      'Log in Now'
                    )}
                  </Link>
                </button>
              </div>
            </div>

            <div
              className={styles.signUpImageContainer}
              style={{
                height: '100%',
              }}
            >
              <Image
                src="/images/buy-guide/signUp.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'cover',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export const getStaticProps = getBasicStaticSideProps(['common', 'buyer_guide', 'index']);

export default BuyerGuide;
